import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Entities from '@shared/entities';
import { catchError, map } from 'rxjs/operators';
import { Observable, lastValueFrom, of } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { Result, ListResponse } from '@shared/models';
import { ConfigService } from '../services/config.service';

@Injectable({
  providedIn: 'root',
})
export class BrandsApi {
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private config: ConfigService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/brands/`;
      }
    });
  }

  async loadBrands(_force: boolean = false): Promise<ListResponse<Entities.Brand>> {
    const result = await lastValueFrom(this.http.get<Result<ListResponse<Entities.Brand>>>(this.serviceUri));
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(result.message || 'Unable to load brands', 'Brands');
    return { records: [] } as ListResponse<Entities.Brand>;
  }

  async create(brand: { manufacturerId: number; name: string }): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceUri, brand));
    if (!result.successful) {
      console.debug('create', result);
    }
    return result;
  }

  async update(id: number, brand: { manufacturerId: number; name: string }): Promise<Result<void>> {
    const result = await lastValueFrom(this.http.put<Result<void>>(this.serviceUri + id, brand));
    if (result.successful) {
      this.log.displaySuccess('Brand updated', 'Brands');
    }
    return result;
  }

  async delete(id: number): Promise<Result<void>> {
    const result = await lastValueFrom(this.http.delete<Result<void>>(this.serviceUri + id));
    if (!result.successful) {
      console.debug('delete', result);
    }
    return result;
  }

  getBrands(): Observable<Entities.Brand[]> {
    return this.http.get<Result<ListResponse<Entities.Brand>>>(this.serviceUri).pipe(
      map((response) => response.data.records),
      catchError(this.handleError<Entities.Brand[]>('getBrands', []))
    );
  }

  getBrandsByManufacturer(manufacturerId: number): Observable<Entities.Brand[]> {
    return this.http.get<Result<ListResponse<Entities.Brand>>>(`${this.serviceUri}manufacturer/${manufacturerId}`).pipe(
      map((response) => response.data.records),
      catchError(this.handleError<Entities.Brand[]>('getBrandsByManufacturer', []))
    );
  }

  getBrand(id: number): Observable<Entities.Brand | undefined> {
    return this.http.get<Result<Entities.Brand>>(this.serviceUri + id).pipe(
      map((response) => response.data),
      catchError(this.handleError<Entities.Brand | undefined>('getBrand', undefined))
    );
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      this.log.logError(error);
      return of(result as T);
    };
  }
}
